import React from "react";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { Box, Typography, Paper, IconButton, Divider } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import MemoryGame from "../../Memory/MemoryGame";
import { Menu as MenuIcon, PersonOutline } from "@mui/icons-material";

// Import the images directly
import drFieldsImage from "./Data/Images/Afro/drfields.jpg";
import hazelJohnsonBrownImage from "./Data/Images/Afro/hazel.jpg";
import adahBelleThomsImage from "./Data/Images/Afro/adah.jpg";
import susieKingTaylorImage from "./Data/Images/Afro/susie.jpg";
import mabelStaupersImage from "./Data/Images/Afro/mabel.jpg";
import maryElizabethCarnegieImage from "./Data/Images/Afro/mary.jpg";
import estelleMasseyOsborneImage from "./Data/Images/Afro/estelle.jpg";
import harrietTubmanImage from "./Data/Images/Afro/harriet.jpg";

// Create a custom theme based on Afro News styling
const theme = createTheme({
  palette: {
    primary: {
      main: "#3D59A3", // Blue for Afro News
    },
    secondary: {
      main: "#111111", // Dark gray (almost black) for headers and text
    },
    background: {
      default: "#f6f6f6", // Light gray background
      paper: "#ffffff",
    },
  },
  typography: {
    fontFamily: '"Roboto", "Arial", sans-serif',
    h5: {
      fontWeight: 700,
    },
    h6: {
      fontWeight: 700,
    },
    button: {
      textTransform: "uppercase",
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 4,
  },
  components: {
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          boxShadow: "0px 2px 6px rgba(0,0,0,0.05)",
        },
      },
    },
  },
});

/**
 * Black Nurses Memory Demo Component
 *
 * This component showcases the Memory Game with content focused on
 * influential Black nurses in history.
 */
const AfroMemoryDemo = () => {
  // Black Nurses memory items
  const blackNursesItems = [
    {
      id: 1,
      title: "Dr. Sheldon D. Fields",
      image: drFieldsImage,
      isImagePath: true,
    },
    {
      id: 2,
      title: "Hazel W. Johnson-Brown",
      image: hazelJohnsonBrownImage,
      isImagePath: true,
    },
    {
      id: 3,
      title: "Adah Belle Thoms",
      image: adahBelleThomsImage,
      isImagePath: true,
    },
    {
      id: 4,
      title: "Susie King Taylor",
      image: susieKingTaylorImage,
      isImagePath: true,
    },
    {
      id: 5,
      title: "Mabel K. Staupers",
      image: mabelStaupersImage,
      isImagePath: true,
    },
    {
      id: 6,
      title: "Mary Elizabeth Carnegie",
      image: maryElizabethCarnegieImage,
      isImagePath: true,
    },
    {
      id: 7,
      title: "Estelle Massey Osborne",
      image: estelleMasseyOsborneImage,
      isImagePath: true,
    },
    {
      id: 8,
      title: "Harriet Tubman",
      image: harrietTubmanImage,
      isImagePath: true,
    },
  ];

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline enableColorScheme />

      <Box sx={{ bgcolor: "background.default", pb: 4 }}>
        <Paper
          sx={{
            maxWidth: "sm",
            margin: "0 auto",
            position: "relative",
            overflow: "hidden",
            bgcolor: "#111111",
            boxShadow: "0px 0px 10px rgba(0,0,0,0.1)",
          }}
        >
          {/* Top navigation bar - Updated to match Afro News design */}
          <Box
            sx={{
              bgcolor: "#111", // Dark gray background (instead of pure black)
              color: "white",
              p: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid #333",
            }}
          >
            {/* Logo area */}
            <Box
              component="img"
              src="/assets/customer-logos/afro-logo-vert.webp"
              alt="Afro News Logo"
              sx={{
                height: 65,
                width: "auto",
                objectFit: "contain",
              }}
            />

            {/* Icons on right */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <IconButton color="inherit" aria-label="menu" sx={{ mr: 1 }}>
                <MenuIcon sx={{ fontSize: 28 }} />
              </IconButton>
              <IconButton color="inherit" aria-label="account">
                <PersonOutline sx={{ fontSize: 28 }} />
              </IconButton>
            </Box>
          </Box>

          {/* Content container with consistent padding */}
          <Box sx={{ p: 2 }}>
            {/* Games section title */}
            <Typography
              variant="h5"
              component="div"
              sx={{
                fontWeight: 700,
                color: "#3D59A3", // Blue color for header
                mb: 1,
                fontSize: "1.8rem",
                letterSpacing: "0.02em",
                textTransform: "uppercase",
              }}
            >
              GAMES
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {/* Game container with consistent styling */}
            <Box sx={{ mb: 4, borderRadius: 1, overflow: "hidden" }}>
              <MemoryGame
                customItems={blackNursesItems}
                customTitle="Pioneering Black Nurses in History"
                subtitle="Match each nurse's name with their image"
                shareText="Afro News Memory Match"
                successBackgroundColor="#3D59A3"
                shareButtonColor={["#ffad00", "#eb9800"]}
                logoPath="/assets/customers/afro-news/logo.png"
                publicationId="BlackNursesMemoryGame"
                hideTitle={true} // Hide the game title to let our custom styling handle it
              />
            </Box>
          </Box>
        </Paper>

        {/* Disclaimer at the bottom */}
        <Box
          sx={{
            textAlign: "center",
            p: 2,
            mt: 2,
            color: "#666",
            fontSize: "0.8rem",
          }}
        >
          Demo created by Flying Comet Games for Afro News. Not for
          distribution.
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default AfroMemoryDemo;
